<template>
  <el-form
      @submit.prevent="submit(false)"
      :class="{ ar: $ctx.locale == 'ar' }"
      ref="zeissCardForm"
      :model="card"
      :rules="rules"
      label-position="left"
      class="m-8 bg-white shadow-md p-6 zeiss-card-form"
      novalidate
  >
    <h1 class="text-2xl font-bold text-blue-900 mb-6">{{ t(isEdit ? 'editZeissCard' : 'zeissCard') }}</h1>

    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <div class="flex flex-col gap-3 max-w-xl">
      <el-form-item :label="t('customerName')" prop="customerName" label-width="140px">
        <el-autocomplete
            tabindex="1"
            autofocus
            v-model="card.customerName"
            :fetch-suggestions="searchCustomerNames"
            :trigger-on-focus="false"
            :debounce="200"
            maxlength="255"
            style="width: 260px"
        />
      </el-form-item>

      <el-form-item :label="t('orderNumber')" prop="orderNumber" label-width="140px">
        <el-input
            tabindex="2"
            v-model="card.orderNumber"
            maxlength="255"
            style="width: 260px"
        />
      </el-form-item>
    </div>

    <!-- Results: same controls as the Distance Glasses section of the examination form, IPD replaced by ADD -->
    <div class="grid grid-cols-5 gap-y-2 gap-x-4 items-center mt-4 max-w-3xl" style="direction: ltr">
      <div class="justify-self-end font-bold text-lg col-span-1"></div>
      <div class="col-span-4 font-bold text-lg">{{ t('results') }}</div>

      <div></div>
      <div class="justify-self-center font-bold"><span class="text-red-500 mr-0.5">*</span>SPH</div>
      <div class="justify-self-center font-bold"><span class="text-red-500 mr-0.5">*</span>CYL</div>
      <div class="justify-self-center font-bold">AXIS</div>
      <div class="justify-self-center font-bold">ADD</div>

      <div class="justify-self-end font-bold">OD</div>
      <el-form-item prop="odSPH" class="!mb-0 result-item">
        <CustomNumberInput
            size="large"
            tabindex="3"
            v-model="card.odSPH"
            :step="0.25"
            :precision="2"
            controls-position="right"
        />
      </el-form-item>
      <el-form-item prop="odCYL" class="!mb-0 result-item">
        <CustomNumberInput
            size="large"
            tabindex="4"
            v-model="card.odCYL"
            :step="0.25"
            :precision="2"
            controls-position="right"
        />
      </el-form-item>
      <el-form-item prop="odAXIS" class="!mb-0 result-item">
        <CustomNumberInput
            size="large"
            tabindex="5"
            v-model="card.odAXIS"
            :step="5"
            :min="0"
            :max="180"
            :precision="0"
            controls-position="right"
        />
      </el-form-item>
      <CustomNumberInput
          size="large"
          tabindex="6"
          v-model="card.odADD"
          :step="0.25"
          :precision="2"
          controls-position="right"
      />

      <div class="justify-self-end font-bold">OS</div>
      <el-form-item prop="osSPH" class="!mb-0 result-item">
        <CustomNumberInput
            size="large"
            tabindex="7"
            v-model="card.osSPH"
            :step="0.25"
            :precision="2"
            controls-position="right"
        />
      </el-form-item>
      <el-form-item prop="osCYL" class="!mb-0 result-item">
        <CustomNumberInput
            size="large"
            tabindex="8"
            v-model="card.osCYL"
            :step="0.25"
            :precision="2"
            controls-position="right"
        />
      </el-form-item>
      <el-form-item prop="osAXIS" class="!mb-0 result-item">
        <CustomNumberInput
            size="large"
            tabindex="9"
            v-model="card.osAXIS"
            :step="5"
            :min="0"
            :max="180"
            :precision="0"
            controls-position="right"
        />
      </el-form-item>
      <CustomNumberInput
          size="large"
          tabindex="10"
          v-model="card.osADD"
          :step="0.25"
          :precision="2"
          controls-position="right"
      />
    </div>

    <div class="flex flex-col gap-2 mt-8 max-w-3xl">
      <el-form-item :label="t('lensType')" prop="lensType" label-width="140px">
        <el-select
            tabindex="11"
            clearable
            filterable
            allow-create
            default-first-option
            v-model="card.lensType"
            placeholder=" "
            class="zeiss-select"
        >
          <el-option
              v-for="option in settings.lensTypes"
              :key="option"
              :label="option"
              :value="option"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="t('practitioner')" prop="practitioner" label-width="140px">
        <el-select
            tabindex="12"
            clearable
            filterable
            allow-create
            default-first-option
            v-model="card.practitioner"
            placeholder=" "
            class="zeiss-select"
        >
          <el-option
              v-for="option in settings.practitioners"
              :key="option"
              :label="option"
              :value="option"
          />
        </el-select>
      </el-form-item>
    </div>

    <div class="w-full flex flex-col input-section mt-24">
      <div class="flex flex-col md:flex-row items-center justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="t('saveAndPrint')"
              @click.prevent="submit(true)"
              :disabled="isSubmitting"
          >
            <span class="flex gap-1">
              <font-awesome-icon icon="save"/>
              <font-awesome-icon icon="print"/>
            </span>
          </horizontal-button>
        </div>

        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.save')"
              isSuccess
              faIcon="save"
              @click.prevent="submit(false)"
              :disabled="isSubmitting"
              :loading="isSubmitting"
          />
        </div>

        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm"
          />
        </div>
      </div>
    </div>
  </el-form>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import type {ElForm} from 'element-plus'

import ZeissCard, {zeissCardI18nMessages, ZeissCardSettings} from '@/models/customer/ZeissCard'

import HorizontalButton from '@/components/HorizontalButton.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import CustomNumberInput from '@/views/examinations/CustomNumberInput.vue'

function cardFromHistoryState(): ZeissCard {
  const state = window.history.state as {card?: Record<string, unknown>} | null
  return state?.card ? ZeissCard.from(state.card) : new ZeissCard()
}

export default defineComponent({
  name: 'new-zeiss-card',

  components: {
    HorizontalButton,
    AlertBadge,
    CustomNumberInput
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {...zeissCardI18nMessages.en},
        ar: {...zeissCardI18nMessages.ar}
      }
    })

    return {t}
  },

  data() {
    return {
      card: cardFromHistoryState(),
      settings: {practitioners: [], lensTypes: []} as ZeissCardSettings,
      isSubmitting: false,
      error: {title: '', body: ''},
      rules: {
        customerName: [
          {
            required: true,
            message: this.t('customerNameRequired'),
            trigger: 'blur'
          }
        ],
        ...Object.fromEntries(
            ['odSPH', 'odCYL', 'osSPH', 'osCYL'].map((field) => [
              field,
              [{required: true, type: 'number', message: this.t('fieldRequired'), trigger: 'change'}]
            ])
        ),
        lensType: [
          {required: true, message: this.t('lensTypeRequired'), trigger: 'change'}
        ],
        practitioner: [
          {required: true, message: this.t('practitionerRequired'), trigger: 'change'}
        ]
      }
    }
  },

  computed: {
    editID(): string {
      const id = this.$route.params.card_id
      return typeof id === 'string' ? id : ''
    },
    isEdit(): boolean {
      return this.editID !== ''
    }
  },

  async beforeMount() {
    await Promise.all([
      this.fetchSettings(),
      this.isEdit ? this.fetchCard() : Promise.resolve()
    ])
  },

  methods: {
    async fetchCard() {
      try {
        const resp = await this.$http.get<ZeissCard>(`${ZeissCard.ENDPOINT}/${this.editID}`)
        this.card = ZeissCard.from(resp)
      } catch (err) {
        const error = err as {title: string; body: string}
        this.error.title = error.title
        this.error.body = error.body
      }
    },

    async fetchSettings() {
      try {
        this.settings = await this.$http.get<ZeissCardSettings>(ZeissCard.SETTINGS_ENDPOINT)
      } catch (err) {
        const error = err as {title: string; body: string}
        this.error.title = error.title
        this.error.body = error.body
      }
    },

    async searchCustomerNames(query: string, cb: (items: {value: string}[]) => void) {
      try {
        const names = await this.$http.get<string[]>(ZeissCard.CUSTOMER_NAMES_ENDPOINT, {
          params: {query}
        })
        cb(names.map((value) => ({value})))
      } catch {
        cb([])
      }
    },

    clearForm() {
      const card = new ZeissCard()
      if (this.isEdit) {
        card.id = this.card.id
      }
      this.card = card
      this.error = {title: '', body: ''}
      const form = this.$refs.zeissCardForm as typeof ElForm | undefined
      form?.clearValidate()
    },

    async submit(print: boolean) {
      const form = this.$refs.zeissCardForm as typeof ElForm
      form.validate(async (valid: boolean) => {
        if (!valid) {
          return false
        }

        this.isSubmitting = true
        try {
          const saved = this.isEdit
              ? await this.$http.put<ZeissCard>(`${ZeissCard.ENDPOINT}/${this.editID}`, this.card)
              : await this.$http.post<ZeissCard>(ZeissCard.ENDPOINT, this.card)
          this.$alertModal.showSuccess({
            title: this.t(this.isEdit ? 'updated' : 'created'),
            body: ''
          })

          if (print) {
            await this.$router.replace({
              path: this.$Route.EXAMINATIONS_ZEISS_CARDS_CARD_PRINT.replace(':card_id', saved.id ?? ''),
              state: ZeissCard.toHistoryState(saved)
            })
          } else {
            await this.$router.replace(this.$Route.EXAMINATIONS_ZEISS_CARDS)
          }
        } catch (err) {
          const error = err as {title: string; body: string}
          this.error.title = error.title
          this.error.body = error.body
          document.getElementById('main')?.scrollTo({top: 0})
        } finally {
          this.isSubmitting = false
        }
        return true
      })
    }
  }
})
</script>

<style scoped>
.zeiss-card-form :deep(.result-item .el-form-item__content) {
  display: block;
}

.zeiss-card-form :deep(.el-form-item__label) {
  font-size: 1rem;
  color: #1f2937;
}

.zeiss-card-form :deep(.zeiss-select) {
  width: 100%;
  min-width: 320px;
  max-width: 480px;
}
</style>
